<template>
  <div class="color-extractor">
    <!-- <div class="page-header">
      <h2>{{ $t('page.title') }}</h2>
      <p>{{ $t('page.subtitle') }}</p>
    </div> -->
    
    <div class="main-content">
      <!-- 左侧面板 -->
      <div class="left-panel">
        <ImageUploader />
        <GridSettings />
        <ColorPicker />
        <CustomColorManager />
      </div>
      
      <!-- 右侧面板 -->
      <div class="right-panel">
        <UsageGuide />
        <PreviewGenerator />
        <ImageColorList />
      </div>
    </div>
    
    <!-- 底部操作栏 -->
    <div class="bottom-actions">
      <div class="action-info">
        <span v-if="hasImage">
          {{ $t('actions.uploaded') }} {{ uploadedImageName }}
        </span>
        <span v-if="hasExtractedColors">
          {{ $t('actions.extracted') }} {{ gridSize.width }} × {{ gridSize.height }} {{ $t('common.colors') }} ({{ uniqueColorsCount }} {{ $t('common.colors') }})
        </span>
        <span v-if="hasCustomColors">
          {{ $t('actions.customColors') }} {{ customColorsCount }} {{ $t('common.items') }}
        </span>
      </div>
      
      <div class="action-buttons">
        <button
          class="btn-secondary"
          @click="clearAll"
          :disabled="!hasImage && !hasCustomColors"
        >
          {{ $t('actions.clearAll') }}
        </button>
        <button
          class="btn-primary"
          @click="exportData"
          :disabled="!hasExtractedColors && !hasCustomColors"
        >
          {{ $t('actions.exportData') }}
        </button>
        <input ref="importInput" type="file" accept="application/json" style="display:none" @change="onImportFile" />
        <button class="btn-primary" @click="triggerImport">{{ $t('actions.importData') }}</button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useColorStore } from '../stores/colorStore'
import { usePreviewStore } from '../stores/previewStore'
import ImageUploader from '../components/ImageUploader.vue'
import GridSettings from '../components/GridSettings.vue'
import CustomColorManager from '../components/CustomColorManager.vue'
import ColorPicker from '../components/ColorPicker.vue'
import ImageColorList from '../components/ImageColorList.vue'
import PreviewGenerator from '../components/PreviewGenerator.vue'
import UsageGuide from '../components/UsageGuide.vue'

const { t } = useI18n()

const colorStore = useColorStore()
const previewStore = usePreviewStore()
const importInput = ref<HTMLInputElement | null>(null)

const hasImage = computed(() => colorStore.hasImage)
const hasExtractedColors = computed(() => !!colorStore.extractedColors)
const hasCustomColors = computed(() => colorStore.hasCustomColors)
const uploadedImage = computed(() => colorStore.uploadedImage)
const gridSize = computed(() => colorStore.gridSize)
const customColors = computed(() => colorStore.customColors)

const uploadedImageName = computed(() => {
  return uploadedImage.value?.name || t('common.unknown')
})

const customColorsCount = computed(() => {
  return customColors.value.length
})

// 计算唯一颜色数量
const uniqueColorsCount = computed(() => {
  if (!colorStore.extractedColors) return 0
  
  const colorSet = new Set<string>()
  
  for (let y = 0; y < colorStore.extractedColors.height; y++) {
    for (let x = 0; x < colorStore.extractedColors.width; x++) {
      const pixel = colorStore.extractedColors.pixels[y][x]
      const key = `${pixel.r}-${pixel.g}-${pixel.b}`
      colorSet.add(key)
    }
  }
  
  return colorSet.size
})

function clearAll() {
  if (confirm(t('actions.confirmClear'))) {
    colorStore.clearAll()
  }
}

async function exportData() {
  // 将已上传原图导出为 base64（如有）
  let imageBase64: string | null = null
  try {
    if (colorStore.uploadedImage) {
      imageBase64 = await fileToDataURL(colorStore.uploadedImage)
    }
  } catch {}

  const data = {
    version: 1,
    timestamp: new Date().toISOString(),
    // 配置
    gridSize: gridSize.value,
    previewPixelSize: colorStore.previewPixelSize,
    colorMatchingAlgorithm: colorStore.colorMatchingAlgorithm,
    mergeMode: colorStore.mergeMode,
    mergeThreshold: colorStore.mergeThreshold,
    baseFabricColor: colorStore.baseFabricColor,
    currentArtStyle: colorStore.getCurrentArtStyle(),
    currentBrand: colorStore.currentBrand,
    // 数据
    customColors: customColors.value,
    extractedColors: colorStore.extractedColors, // 可选：体积较大
    imageBase64
  }

  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
  const link = document.createElement('a')
  link.download = `color-extractor-data-${new Date().toISOString().split('T')[0]}.json`
  link.href = URL.createObjectURL(blob)
  link.click()
  URL.revokeObjectURL(link.href)
}

function triggerImport() {
  importInput.value?.click()
}

async function onImportFile(evt: Event) {
  const input = evt.target as HTMLInputElement
  const file = input.files && input.files[0]
  if (!file) return
  try {
    const text = await file.text()
    const payload = JSON.parse(text)

    // 1) 恢复配置
    if (payload.gridSize?.width && payload.gridSize?.height) {
      colorStore.setGridSize(payload.gridSize.width, payload.gridSize.height)
    }
    if (typeof payload.previewPixelSize === 'number') {
      colorStore.setPreviewPixelSize(payload.previewPixelSize)
    }
    if (payload.colorMatchingAlgorithm) {
      colorStore.setColorMatchingAlgorithm(payload.colorMatchingAlgorithm)
    }
    if (payload.mergeMode) {
      colorStore.setMergeMode(payload.mergeMode)
    }
    if (typeof payload.mergeThreshold === 'number') {
      colorStore.setMergeThreshold(payload.mergeThreshold)
    }
    if (payload.baseFabricColor === null || payload.baseFabricColor?.hex) {
      colorStore.setBaseFabricColor(payload.baseFabricColor)
    }

    // 2) 恢复原图（若存在）
    if (payload.imageBase64 && typeof payload.imageBase64 === 'string') {
      const importedFile = dataURLtoFile(payload.imageBase64, `imported-${Date.now()}.png`)
      colorStore.setImage(importedFile)
    }

    // 3) 恢复自定义颜色
    colorStore.clearCustomColors()
    if (Array.isArray(payload.customColors)) {
      for (const c of payload.customColors) {
        if (typeof c?.r === 'number' && typeof c?.g === 'number' && typeof c?.b === 'number' && typeof c?.hex === 'string') {
          colorStore.addCustomColor({ r: c.r, g: c.g, b: c.b, hex: c.hex, name: c.name })
        }
      }
    }

    // 4) 触发预览渲染
    await previewStore.renderAllPreviews()
    alert(t('actions.importSuccess'))
  } catch (e) {
    console.error('导入失败', e)
    alert(t('actions.importError'))
  } finally {
    if (importInput.value) importInput.value.value = ''
  }
}

function fileToDataURL(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result))
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}

function dataURLtoFile(dataUrl: string, filename: string): File {
  const arr = dataUrl.split(',')
  const mimeMatch = arr[0].match(/:(.*?);/)
  const mime = mimeMatch ? mimeMatch[1] : 'application/octet-stream'
  const bstr = atob(arr[1])
  let n = bstr.length
  const u8arr = new Uint8Array(n)
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n)
  }
  return new File([u8arr], filename, { type: mime })
}
</script>

<style scoped>
.color-extractor {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.page-header {
  text-align: center;
  margin-bottom: 1rem;
}

.page-header h2 {
  color: #333;
  margin-bottom: 0.5rem;
}

.page-header p {
  color: #6c757d;
  font-size: 1.1rem;
}

.main-content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
}

.left-panel,
.right-panel {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.bottom-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  margin-top: 1rem;
}

.action-info {
  display: flex;
  gap: 1.5rem;
  color: #6c757d;
  font-size: 0.9rem;
}

.action-buttons {
  display: flex;
  gap: 1rem;
}

/* 响应式设计 */
@media (max-width: 1200px) {
  .main-content {
    grid-template-columns: 1fr;
  }
  
  .left-panel,
  .right-panel {
    order: 0;
  }
}

@media (max-width: 768px) {
  .bottom-actions {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }
  
  .action-info {
    flex-direction: column;
    gap: 0.5rem;
    text-align: center;
  }
  
  .action-buttons {
    justify-content: center;
  }
}
</style>
